import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import {
	getDefaultPassengerInfo,
	PassengerDetail,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerDetail';
import { passengerTicketIsEmpty } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Passengers/PassengerTabHelpers';
import { bookingType } from '../Enums';
import {
	AdditionalBookingOptionEntity,
	FerryTripEntity,
	TowOnEntity,
	CargoEntity,
	FreightDetailEntity,
} from 'Models/Entities';
import * as Enums from '../Enums';
import { isNotNullOrUndefined, isNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { getFullName } from 'Util/StringUtils';
import * as BookingSummaryDto from 'Views/Components/_HumanWritten/BookingSummaryDto';
import { BookingSummary } from 'Views/Components/_HumanWritten/BookingSummaryDto';
import * as FetchBookingsWithFilters from 'Util/_HumanWritten/Bookings/FetchBookingsWithFilters';
import RouteEntity from './RouteEntity';

export type CustomBookingStatus = Enums.bookingStatus | 'INVOICED' | 'AWAITING' | 'PAID' | '';

/** One fare type's potential fees, shown before an edit is made. */
export interface FareTypeFee {
	passengerClassId: string;
	passengerClassName: string | null;
	passengerCount: number;
	/** Null when no alteration rule matches this fare type — changing it is charged nothing. */
	alterationFeeAmount: number | null;
	alterationStaticFee: boolean;
	/**
	 * Null both when no rule matches and when the site does not charge for removing a passenger — in
	 * either case removing one of these passengers costs nothing.
	 */
	removalFeeAmount: number | null;
	removalStaticFee: boolean;
}

export interface IBookingEntityAttributes extends IModelAttributes {
	checkedIn: boolean;
	desynced: boolean;
	humanReadableId: string;
	legacyId: string;
	isInvoiced: boolean;

	alterations: Array<Models.AlterationEntity | Models.IAlterationEntityAttributes>;
	notes: Array<Models.NoteBooking | Models.INoteBookingAttributes>;
	checkInStatusHistories: Array<Models.CheckInStatusHistoryEntity | Models.ICheckInStatusHistoryEntityAttributes>;
	checkInStatusId?: string;
	checkInStatus?: Models.CheckInStatusEntity | Models.ICheckInStatusEntityAttributes;
	userId: string;
	user: Models.UserEntity | Models.IUserEntityAttributes;
	returnBookingForId?: string;
	returnBookingFor?: Models.BookingEntity | Models.IBookingEntityAttributes;
	returnBooking?: Models.BookingEntity | Models.IBookingEntityAttributes;
	smsDeliveryRecords: Array<Models.SmsDeliveryRecordEntity | Models.ISmsDeliveryRecordEntityAttributes>;
	bookingSummaries: BookingSummaryDto.BookingSummaries;
	firstTimeOnRoute: boolean;
	/**
	 * This is a custom GraphQL resolver.
	 *
	 * It looks at the status of the booking's alterations to determine it's current status.
	 */
	bookingStatus?: Enums.bookingStatus;
	transactionStatus?: Enums.bookingTransactionStatus;
	transactionStatuses?: Enums.bookingTransactionStatus[];
	isEventBooking: boolean;
	isTransferred?: boolean;
	/**
	 * This is a custom GraphQL resolver.
	 *
	 * It looks at the status of the booking's alterations and transactions to determine the status to display. Return
	 * value can be PENDING_INVOICE/INVOICED/BOOKED/RESERVED/CANCELLED/EXPIRED_RESERVATION or empty.
	 */
	displayedStatus?: CustomBookingStatus;
	/**
	 * Custom GraphQL resolver.
	 *
	 * True if booking has an alteration linked to a transaction with status INVOICED or PENDING_INVOICE.
	 */
	invoiced?: boolean;
	bulkBookingId?: string;
	qrCodeId?: string;
	/**
	 * Custom GraphQL resolver.
	 *
	 * The summary of the booking only using BOOKED alterations.
	 */
	bookedSummary?: BookingSummaryDto.BookingSummary;
	/**
	 * Custom GraphQL resolver.
	 *
	 * The summary of the booking (can be event or ferry booking).
	 */
	bookingListSummary?: FetchBookingsWithFilters.BookingListSummaryDto;
	/**
	 * Custom GraphQL resolver.
	 *
	 * The cancellation percentage that applies to this booking
	 */
	cancellationFee?: number;
	/**
	 * Custom GraphQL resolver.
	 *
	 * The alteration fee that applies to this booking
	 */
	alterationFee?: number;
	/**
	 * The percentage applied by the alteration (EDIT) cutoff that currently matches this booking.
	 * Undefined when no cutoff applies or the cutoff is a flat/static fee (use alterationFee).
	 */
	alterationFeePercentage?: number;
	/**
	 * Hours before departure within which the alteration (EDIT) cutoff fee applies. Undefined when
	 * no cutoff applies or it has no time window. Used to explain the fee rule on the edit flow.
	 */
	alterationCutoffHours?: number;
	/**
	 * True when the alteration (EDIT) cutoff matching this booking is an any-time rule — the fee
	 * applies however far ahead the trip is changed, so there is no window to name. Carried
	 * separately because `alterationCutoffHours` is 0 for such a rule, which cannot be told apart
	 * from a genuine zero-hour one.
	 */
	alterationCutoffAppliesAnytime?: boolean;
	/**
	 * What each fare type on this booking would be charged — for an alteration, and for having one of
	 * its passengers removed. Empty when fare types are switched off.
	 */
	fareTypeFees?: FareTypeFee[];
	/**
	 * True when the alteration (EDIT) cutoff matching this booking waives the fee for moving to an
	 * earlier (closer) trip. Lets the edit flow reassure the customer that moving forward is free.
	 */
	moveToEarlierTimeFeeExempt?: boolean;
	/**
	 * True when the alteration (EDIT) cutoff matching this booking waives the fee for moving to a
	 * later trip on the same day. Only same-day later moves are exempt.
	 */
	moveToLaterTimeFeeExempt?: boolean;
	/**
	 * Custom GraphQL resolver.
	 *
	 * The hours before departure for the cancellation fee
	 */
	cancellationCutoffHours?: number;
}
@entity('BookingEntity', 'Booking')
export default class BookingEntity extends Model implements IBookingEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * True if user has been checked into the trip, false otherwise
	 */
	@observable
	@attribute<BookingEntity, boolean>()
	public checkedIn: boolean;

	/**
	 * A boolean for if the linked booking has the same alteration information, and therefore the associated trip can also be edited.
	 */
	@observable
	@attribute<BookingEntity, boolean>()
	public desynced: boolean;

	/**
	 * Converts GUID into a 9 digit  number
	 */
	@observable
	@attribute<BookingEntity, string>()
	public humanReadableId: string;

	@observable
	@attribute<BookingEntity, number>()
	public legacyId: string;

	/**
	 * Whether the booking has been invoiced or not.
	 */
	@observable
	@attribute<BookingEntity, boolean>()
	public isInvoiced: boolean;

	@observable
	@attribute<BookingEntity, string>()
	public bulkBookingId?: string;

	@observable
	@attribute<BookingEntity, string>()
	public qrCodeId?: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public alterations: Models.AlterationEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public notes: Models.NoteBooking[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public checkInStatusHistories: Models.CheckInStatusHistoryEntity[] = [];

	@observable
	@attribute()
	public checkInStatusId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public checkInStatus?: Models.CheckInStatusEntity;

	@observable
	@attribute()
	public userId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user: Models.UserEntity;

	@observable
	@attribute()
	public returnBookingForId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public returnBookingFor: Models.BookingEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public returnBooking?: Models.BookingEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public smsDeliveryRecords: Models.SmsDeliveryRecordEntity[] = [];

	public bookingSummaries: BookingSummaryDto.BookingSummaries;

	/**
	 * This is a custom GraphQL resolver (see BookingEntityType.cs).
	 *
	 * Returns true if this booking is the first time on a route compared the user's booking history.
	 */
	public firstTimeOnRoute: boolean;

	public bookingStatus?: Enums.bookingStatus;

	public displayedStatus?: CustomBookingStatus;

	public isEventBooking: boolean;

	public invoiced?: boolean;

	public isTransferred?: boolean;

	@observable
	public bookedSummary?: BookingSummaryDto.BookingSummary;

	public bookingListSummary?: FetchBookingsWithFilters.BookingListSummaryDto;

	public transactionStatus?: Enums.bookingTransactionStatus;

	// All display statuses for the booking. Usually one (matching transactionStatus), but a cancelled
	// booking with an unpaid/invoiced cancellation fee carries two: CANCELLED plus AWAITING or
	// INVOICED. Rendered as one tag each on the detailed view.
	public transactionStatuses?: Enums.bookingTransactionStatus[];

	public alterationFee?: number;

	public alterationFeePercentage?: number;

	public alterationCutoffHours?: number;

	public alterationCutoffAppliesAnytime?: boolean;

	public fareTypeFees?: FareTypeFee[];

	public moveToEarlierTimeFeeExempt?: boolean;

	public moveToLaterTimeFeeExempt?: boolean;

	public cancellationFee?: number;

	public cancellationCutoffHours?: number;

	constructor(attributes?: Partial<IBookingEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IBookingEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.checkedIn !== undefined) {
				this.checkedIn = attributes.checkedIn;
			}
			if (attributes.desynced !== undefined) {
				this.desynced = attributes.desynced;
			}
			if (attributes.humanReadableId !== undefined) {
				this.humanReadableId = attributes.humanReadableId;
			}
			if (attributes.legacyId !== undefined) {
				this.legacyId = attributes.legacyId;
			}
			if (attributes.isInvoiced !== undefined) {
				this.isInvoiced = attributes.isInvoiced;
			}
			if (attributes.bulkBookingId !== undefined) {
				this.bulkBookingId = attributes.bulkBookingId;
			}
			if (attributes.qrCodeId !== undefined) {
				this.qrCodeId = attributes.qrCodeId;
			}
			if (attributes.alterations !== undefined && Array.isArray(attributes.alterations)) {
				if (!this.alterations) {
					this.alterations = [];
				}
				for (const model of attributes.alterations) {
					if (model instanceof Models.AlterationEntity) {
						this.alterations.push(model);
					} else {
						this.alterations.push(new Models.AlterationEntity(model));
					}
				}
			}
			if (attributes.notes !== undefined && Array.isArray(attributes.notes)) {
				if (!this.notes) {
					this.notes = [];
				}
				for (const model of attributes.notes) {
					if (model instanceof Models.NoteBooking) {
						this.notes.push(model);
					} else {
						this.notes.push(new Models.NoteBooking(model));
					}
				}
			}
			if (attributes.checkInStatusHistories !== undefined && Array.isArray(attributes.checkInStatusHistories)) {
				if (!this.checkInStatusHistories) {
					this.checkInStatusHistories = [];
				}
				for (const model of attributes.checkInStatusHistories) {
					if (model instanceof Models.CheckInStatusHistoryEntity) {
						this.checkInStatusHistories.push(model);
					} else {
						this.checkInStatusHistories.push(new Models.CheckInStatusHistoryEntity(model));
					}
				}
			}
			if (attributes.checkInStatusId !== undefined) {
				this.checkInStatusId = attributes.checkInStatusId;
			}
			if (attributes.checkInStatus !== undefined) {
				if (attributes.checkInStatus === null) {
					this.checkInStatus = attributes.checkInStatus;
				} else if (attributes.checkInStatus instanceof Models.CheckInStatusEntity) {
					this.checkInStatus = attributes.checkInStatus;
				} else {
					this.checkInStatus = new Models.CheckInStatusEntity(attributes.checkInStatus);
				}
			}
			if (attributes.userId !== undefined) {
				this.userId = attributes.userId;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
			if (attributes.returnBookingForId !== undefined) {
				this.returnBookingForId = attributes.returnBookingForId;
			}
			if (attributes.returnBookingFor !== undefined) {
				if (attributes.returnBookingFor === null) {
					this.returnBookingFor = attributes.returnBookingFor;
				} else if (attributes.returnBookingFor instanceof Models.BookingEntity) {
					this.returnBookingFor = attributes.returnBookingFor;
					this.returnBookingForId = attributes.returnBookingFor.id;
				} else {
					this.returnBookingFor = new Models.BookingEntity(attributes.returnBookingFor);
					this.returnBookingForId = this.returnBookingFor.id;
				}
			}
			if (attributes.returnBooking !== undefined) {
				if (attributes.returnBooking === null) {
					this.returnBooking = attributes.returnBooking;
				} else if (attributes.returnBooking instanceof Models.BookingEntity) {
					this.returnBooking = attributes.returnBooking;
				} else {
					this.returnBooking = new Models.BookingEntity(attributes.returnBooking);
				}
			}
			if (attributes.smsDeliveryRecords !== undefined && Array.isArray(attributes.smsDeliveryRecords)) {
				if (!this.smsDeliveryRecords) {
					this.smsDeliveryRecords = [];
				}
				for (const model of attributes.smsDeliveryRecords) {
					if (model instanceof Models.SmsDeliveryRecordEntity) {
						this.smsDeliveryRecords.push(model);
					} else {
						this.smsDeliveryRecords.push(new Models.SmsDeliveryRecordEntity(model));
					}
				}
			}
			if (
				isNotNullOrUndefined(attributes.bookingSummaries) &&
				isNotNullOrUndefined(attributes.bookingSummaries.recentSummary)
			) {
				//
				// New alteration booking summary section
				//
				this.bookingSummaries = {
					recentSummary: new BookingSummary({
						userFirstName: attributes.bookingSummaries.recentSummary.userFirstName,
						userLastName: attributes.bookingSummaries.recentSummary.userLastName,
						adultPassengerTickets: attributes.bookingSummaries.recentSummary.adultPassengerTickets,
						childPassengerTickets: attributes.bookingSummaries.recentSummary.childPassengerTickets,
						infantPassengerTickets: attributes.bookingSummaries.recentSummary.infantPassengerTickets,
						passengerDTickets: attributes.bookingSummaries.recentSummary.passengerDTickets,
						passengerETickets: attributes.bookingSummaries.recentSummary.passengerETickets,
						passengerFTickets: attributes.bookingSummaries.recentSummary.passengerFTickets,
						passengerGTickets: attributes.bookingSummaries.recentSummary.passengerGTickets,
						passengerHTickets: attributes.bookingSummaries.recentSummary.passengerHTickets,
						driverFirstName: attributes.bookingSummaries.recentSummary.driverFirstName,
						driverLastName: attributes.bookingSummaries.recentSummary.driverLastName,
						driverPhone: attributes.bookingSummaries.recentSummary.driverPhone,
						ferryTrip: new FerryTripEntity(attributes.bookingSummaries.recentSummary.ferryTrip),
						totalCost: attributes.bookingSummaries.recentSummary.totalCost,
						maxRefund: attributes.bookingSummaries.recentSummary.maxRefund,
						paidBookingPrice: attributes.bookingSummaries.recentSummary.paidBookingPrice,
						nonRefundableAmount: attributes.bookingSummaries.recentSummary.nonRefundableAmount,
						discountAmount: attributes.bookingSummaries.recentSummary.discountAmount,
						additionalBookingOptions: [],
						note: attributes.bookingSummaries.recentSummary.note,
						// Feature: Multi-stop - booking entity assign
						startStopId: attributes.bookingSummaries.recentSummary.startStopId,
						endStopId: attributes.bookingSummaries.recentSummary.endStopId,
						freightDetail: isNotNullOrUndefined(attributes.bookingSummaries?.recentSummary?.freightDetail)
							? new FreightDetailEntity(attributes.bookingSummaries?.recentSummary?.freightDetail)
							: undefined,
						// Primary booking contact ("name on booking"). The server sends these on every
						// summary, but this hand-written copy used to drop them, so editing a booking FROM
						// THE CART arrived at the wizard with a blank name and BookingDetailsTab's
						// pre-fill then replaced it with the account holder's name. `bookedSummary` is
						// assigned wholesale further down, which is why the detailed-view edit was fine.
						primaryFirstName: attributes.bookingSummaries.recentSummary.primaryFirstName,
						primaryLastName: attributes.bookingSummaries.recentSummary.primaryLastName,
						primaryPhone: attributes.bookingSummaries.recentSummary.primaryPhone,
						primaryEmail: attributes.bookingSummaries.recentSummary.primaryEmail,
						alterationContext: attributes.bookingSummaries.recentSummary.alterationContext,
						tripDateTime: attributes.bookingSummaries.recentSummary.tripDateTime,
					}),
				};
				//
				// New alteration booking summary: CargoInfo
				//
				const { cargoInfo: vehicleInfoFromNewSummary } = attributes.bookingSummaries.recentSummary;

				if (!isNotNullOrUndefined(vehicleInfoFromNewSummary)) {
					this.bookingSummaries.recentSummary.cargoInfo = null;
				} else if (vehicleInfoFromNewSummary instanceof CargoEntity) {
					this.bookingSummaries.recentSummary.cargoInfo = attributes.bookingSummaries.recentSummary.cargoInfo;
				} else {
					this.bookingSummaries.recentSummary.cargoInfo = new CargoEntity(vehicleInfoFromNewSummary);
				}
				//
				// New alteration booking summary: TowOnInfo
				//
				const { towOnInfo: trailerInfoFromNewSummary } = attributes.bookingSummaries.recentSummary;

				if (!isNotNullOrUndefined(trailerInfoFromNewSummary)) {
					this.bookingSummaries.recentSummary.towOnInfo = null;
				} else if (trailerInfoFromNewSummary instanceof TowOnEntity) {
					this.bookingSummaries.recentSummary.towOnInfo = trailerInfoFromNewSummary;
				} else {
					this.bookingSummaries.recentSummary.towOnInfo = new TowOnEntity(trailerInfoFromNewSummary);
				}
				//
				// New alteration booking summary: AdditionalBookingOptions
				//
				const { additionalBookingOptions: optionsFromNewSummary } = attributes.bookingSummaries.recentSummary;
				if (Array.isArray(optionsFromNewSummary)) {
					this.bookingSummaries.recentSummary.additionalBookingOptions = optionsFromNewSummary.map(option => {
						return {
							quantity: option.quantity,
							option:
								option.option instanceof AdditionalBookingOptionEntity
									? option.option
									: new AdditionalBookingOptionEntity(option.option),
						};
					});
				}
				//
				// Old alteration booking summary
				//
				if (attributes.bookingSummaries.previousSummary) {
					this.bookingSummaries.previousSummary = new BookingSummary({
						userFirstName: attributes.bookingSummaries.previousSummary.userFirstName,
						userLastName: attributes.bookingSummaries.previousSummary.userLastName,
						adultPassengerTickets: attributes.bookingSummaries.previousSummary.adultPassengerTickets,
						childPassengerTickets: attributes.bookingSummaries.previousSummary.childPassengerTickets,
						infantPassengerTickets: attributes.bookingSummaries.previousSummary.infantPassengerTickets,
						passengerDTickets: attributes.bookingSummaries.previousSummary.passengerDTickets,
						passengerETickets: attributes.bookingSummaries.previousSummary.passengerETickets,
						passengerFTickets: attributes.bookingSummaries.previousSummary.passengerFTickets,
						passengerGTickets: attributes.bookingSummaries.previousSummary.passengerGTickets,
						passengerHTickets: attributes.bookingSummaries.previousSummary.passengerHTickets,
						driverFirstName: attributes.bookingSummaries.previousSummary.driverFirstName,
						driverLastName: attributes.bookingSummaries.previousSummary.driverLastName,
						driverPhone: attributes.bookingSummaries.previousSummary.driverPhone,
						ferryTrip: new FerryTripEntity(attributes.bookingSummaries.previousSummary.ferryTrip),
						totalCost: attributes.bookingSummaries.previousSummary.totalCost,
						paidBookingPrice: attributes.bookingSummaries.previousSummary.paidBookingPrice,
						discountAmount: attributes.bookingSummaries.previousSummary.discountAmount,
						maxRefund: attributes.bookingSummaries.previousSummary.maxRefund,
						nonRefundableAmount: attributes.bookingSummaries.recentSummary.nonRefundableAmount,
						additionalBookingOptions: [],
						freightDetail: isNotNullOrUndefined(attributes.bookingSummaries?.previousSummary?.freightDetail)
							? new FreightDetailEntity(attributes.bookingSummaries?.previousSummary?.freightDetail)
							: undefined,
						primaryFirstName: attributes.bookingSummaries.previousSummary.primaryFirstName,
						primaryLastName: attributes.bookingSummaries.previousSummary.primaryLastName,
						primaryPhone: attributes.bookingSummaries.previousSummary.primaryPhone,
						primaryEmail: attributes.bookingSummaries.previousSummary.primaryEmail,
					});
					//
					// Old alteration booking summary: CargoInfo
					//
					const { cargoInfo: vehicleInfoFromOldSummary } = attributes.bookingSummaries.previousSummary;
					if (!isNotNullOrUndefined(vehicleInfoFromOldSummary)) {
						this.bookingSummaries.previousSummary.cargoInfo = null;
					} else if (vehicleInfoFromOldSummary instanceof CargoEntity) {
						this.bookingSummaries.previousSummary.cargoInfo = vehicleInfoFromOldSummary;
					} else {
						this.bookingSummaries.previousSummary.cargoInfo = new CargoEntity(vehicleInfoFromOldSummary);
					}
					//
					// Old alteration booking summary: TowOnInfo
					//
					const { towOnInfo: trailerInfoFromOldSummary } = attributes.bookingSummaries.previousSummary;
					if (!isNotNullOrUndefined(trailerInfoFromOldSummary)) {
						this.bookingSummaries.previousSummary.towOnInfo = null;
					} else if (trailerInfoFromOldSummary instanceof TowOnEntity) {
						this.bookingSummaries.previousSummary.towOnInfo = trailerInfoFromOldSummary;
					} else {
						this.bookingSummaries.previousSummary.towOnInfo = new TowOnEntity(trailerInfoFromOldSummary);
					}
					//
					// Old alteration booking summary: AdditionalBookingOptions
					//
					const { additionalBookingOptions: optionsFromOldSummary } =
						attributes.bookingSummaries.previousSummary;
					if (Array.isArray(optionsFromOldSummary)) {
						this.bookingSummaries.previousSummary.additionalBookingOptions = optionsFromOldSummary.map(
							option => {
								return {
									quantity: option.quantity,
									option:
										option.option instanceof AdditionalBookingOptionEntity
											? option.option
											: new AdditionalBookingOptionEntity(option.option),
								};
							},
						);
					}
				}
			}
			if (attributes.firstTimeOnRoute !== undefined) {
				this.firstTimeOnRoute = attributes.firstTimeOnRoute;
			}
			if (isNotNullOrUndefined(attributes.bookingStatus)) {
				this.bookingStatus = attributes.bookingStatus;
			}
			if (isNotNullOrUndefined(attributes.displayedStatus)) {
				this.displayedStatus = attributes.displayedStatus;
			}
			if (isNotNullOrUndefined(attributes.isEventBooking)) {
				this.isEventBooking = attributes.isEventBooking;
			}
			if (isNotNullOrUndefined(attributes.invoiced)) {
				this.invoiced = attributes.invoiced;
			}
			if (isNotNullOrUndefined(attributes.isTransferred)) {
				this.isTransferred = attributes.isTransferred;
			}
			if (isNotNullOrUndefined(attributes.bookedSummary)) {
				this.bookedSummary = attributes.bookedSummary;

				this.bookedSummary.ferryTrip = new FerryTripEntity(this.bookedSummary.ferryTrip);
				if (isNotNullOrUndefined(this.bookedSummary.cargoInfo)) {
					this.bookedSummary.cargoInfo = new CargoEntity(this.bookedSummary.cargoInfo);
				}
			}
			if (isNotNullOrUndefined(attributes.bookingListSummary)) {
				this.bookingListSummary = attributes.bookingListSummary;

				this.bookingListSummary.route = new RouteEntity(this.bookingListSummary.route);
			}
			if (isNotNullOrUndefined(attributes.transactionStatus)) {
				this.transactionStatus = attributes.transactionStatus;
			}
			if (isNotNullOrUndefined(attributes.transactionStatuses)) {
				this.transactionStatuses = attributes.transactionStatuses;
			}
			if (isNotNullOrUndefined(attributes.alterationFee)) {
				this.alterationFee = attributes.alterationFee;
			}
			if (isNotNullOrUndefined(attributes.alterationFeePercentage)) {
				this.alterationFeePercentage = attributes.alterationFeePercentage;
			}
			if (isNotNullOrUndefined(attributes.alterationCutoffHours)) {
				this.alterationCutoffHours = attributes.alterationCutoffHours;
			}
			if (isNotNullOrUndefined(attributes.alterationCutoffAppliesAnytime)) {
				this.alterationCutoffAppliesAnytime = attributes.alterationCutoffAppliesAnytime;
			}
			if (isNotNullOrUndefined(attributes.fareTypeFees)) {
				this.fareTypeFees = attributes.fareTypeFees;
			}
			if (isNotNullOrUndefined(attributes.moveToEarlierTimeFeeExempt)) {
				this.moveToEarlierTimeFeeExempt = attributes.moveToEarlierTimeFeeExempt;
			}
			if (isNotNullOrUndefined(attributes.moveToLaterTimeFeeExempt)) {
				this.moveToLaterTimeFeeExempt = attributes.moveToLaterTimeFeeExempt;
			}
			if (isNotNullOrUndefined(attributes.cancellationFee)) {
				this.cancellationFee = attributes.cancellationFee;
			}
			if (isNotNullOrUndefined(attributes.cancellationCutoffHours)) {
				this.cancellationCutoffHours = attributes.cancellationCutoffHours;
			}
		}
	}

	/**
	 * If booking has a vehicle, driver name is used.
	 *
	 * Otherwise user account name is used.
	 */
	public getFullName(fromBookedSummary?: boolean) {
		if (!this.user || (!this.bookingSummaries && !fromBookedSummary)) {
			return null;
		}

		if (fromBookedSummary && !this.bookedSummary) {
			throw new Error('Booked summary missing');
		}

		const { cargoInfo, driverFirstName, driverLastName } =
			fromBookedSummary && this.bookedSummary ? this.bookedSummary : this.bookingSummaries.recentSummary;

		const isVehicleBooking = isNotNullOrUndefined(cargoInfo);
		const name = isVehicleBooking
			? getFullName(driverFirstName, driverLastName, true)
			: this.user.getFullName(true);

		return name;
	}

	/**
	 * Returns text for make of vehicle (if any)
	 *
	 * Returns '-' if not available.
	 */
	public getDisplayVehicleMake(): string {
		if (this.bookedSummary) {
			const { cargoInfo } = this.bookedSummary;
			if (cargoInfo && cargoInfo.cargoType && cargoInfo.cargoType.cargoMake) {
				return cargoInfo.cargoType.cargoMake;
			}
		}

		return '-';
	}

	/**
	 * Returns text for model of vehicle (if any)
	 *
	 * Returns '-' if not available.
	 */
	public getDisplayVehicleModel(): string {
		if (this.bookedSummary) {
			const { cargoInfo } = this.bookedSummary;
			if (cargoInfo && cargoInfo.cargoType && cargoInfo.cargoType.cargoModel) {
				return cargoInfo.cargoType.cargoModel;
			}
		}

		return '-';
	}

	public hasVehicle(): boolean {
		return (
			isNotNullOrUndefined(this.bookedSummary?.cargoInfo) ||
			isNotNullOrUndefined(this.bookingSummaries?.recentSummary?.cargoInfo)
		);
	}

	public hasPassengers(): boolean {
		return (
			(this.bookedSummary?.adultPassengerTickets?.length ?? 0) > 0 ||
			(this.bookedSummary?.childPassengerTickets?.length ?? 0) > 0 ||
			(this.bookedSummary?.infantPassengerTickets?.length ?? 0) > 0 ||
			(this.bookedSummary?.passengerDTickets?.length ?? 0) > 0 ||
			(this.bookedSummary?.passengerETickets?.length ?? 0) > 0 ||
			(this.bookedSummary?.passengerFTickets?.length ?? 0) > 0 ||
			(this.bookedSummary?.passengerGTickets?.length ?? 0) > 0 ||
			(this.bookedSummary?.passengerHTickets?.length ?? 0) > 0
		);
	}

	/**
	 * Create a booking entity with default required values. Particularly for fields in recent summary.
	 */
	static createBlank() {
		return new BookingEntity({
			user: new Models.UserEntity(),
			bookedSummary: new BookingSummary({
				userFirstName: '',
				userLastName: '',
				adultPassengerTickets: [getDefaultPassengerInfo('A', false)],
				childPassengerTickets: [],
				infantPassengerTickets: [],
				passengerDTickets: [],
				passengerETickets: [],
				passengerFTickets: [],
				passengerGTickets: [],
				passengerHTickets: [],
				driverPhone: '',
				driverFirstName: '',
				driverLastName: '',
				ferryTrip: new Models.FerryTripEntity(),
				totalCost: 0,
				paidBookingPrice: 0,
				maxRefund: 0,
				nonRefundableAmount: 0,
				additionalBookingOptions: [],
			}),
		});
	}

	public GetAllPassengerInfosFromBookedSummary(): PassengerDetail[] {
		if (isNullOrUndefined(this.bookedSummary)) {
			return [];
		}

		const {
			adultPassengerTickets,
			childPassengerTickets,
			infantPassengerTickets,
			passengerDTickets,
			passengerETickets,
			passengerFTickets,
			passengerGTickets,
			passengerHTickets,
		} = this.bookedSummary;
		return [
			...(adultPassengerTickets ?? []),
			...(childPassengerTickets ?? []),
			...(infantPassengerTickets ?? []),
			...(passengerDTickets ?? []),
			...(passengerETickets ?? []),
			...(passengerFTickets ?? []),
			...(passengerGTickets ?? []),
			...(passengerHTickets ?? []),
		]
			// "Empty" means no personal details, which is every passenger on a site that does not
			// collect them — and dropping those left a three-passenger booking showing one row. A
			// passenger holding a class or a seat has something to say either way.
			.filter(x => !passengerTicketIsEmpty(x) || stringNotEmpty(x.passengerClassName) || stringNotEmpty(x.seatLabel));
	}

	public typeOfBooking(): bookingType {
		if (this.bookedSummary?.eventId) {
			return 'EVENT_BOOKING';
		}
		if (this.bookedSummary?.freightDetail) {
			return 'FREIGHT';
		}
		if (this.bookedSummary?.cargoInfo) {
			return 'VEHICLE';
		}
		return 'PASSENGER_ONLY';
	}

	public isFreightBooking(): boolean {
		return this.typeOfBooking() === 'FREIGHT';
	}

	public isAnEventBooking(): boolean {
		return this.typeOfBooking() === 'EVENT_BOOKING';
	}
}
